import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useTranslation } from "../i18n/I18nContext";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuLabel
} from "../components/ui/dropdown-menu";
import PurchaseConfirmModal from "../components/PurchaseConfirmModal";
import BundlePackageDetails from "../components/BundlePackageDetails";
import { toast } from "sonner";
import { 
  Calendar, BookOpen, 
  CreditCard, LogOut, ChevronRight, Clock, Gift, CheckCircle, UserCircle, BarChart3,
  Globe, Plus, Check, ChevronDown, Loader2
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Aktif ödeme sağlayıcısı. "monri" = kullanıcıya Monri gösterilir; "stripe" gizli yedek.
const PAYMENT_PROVIDER = "monri";

// Monri hosted form'una gizli HTML form ile POST edip yönlendirir (gizli anahtar tarayıcıya gitmez).
const submitMonriForm = (formUrl, fields) => {
  const form = document.createElement("form");
  form.method = "POST";
  form.action = formUrl;
  form.style.display = "none";
  Object.entries(fields).forEach(([name, value]) => {
    const input = document.createElement("input");
    input.type = "hidden";
    input.name = name;
    input.value = value;
    form.appendChild(input);
  });
  document.body.appendChild(form);
  form.submit();
};

// Öğrenilebilen 6 dil (bayrak + isim)
const LEARNING_LANGS = {
  en: { flag: "🇬🇧", name: "English" },
  es: { flag: "🇪🇸", name: "Español" },
  me: { flag: "🇲🇪", name: "Crnogorski" },
  de: { flag: "🇩🇪", name: "Deutsch" },
  it: { flag: "🇮🇹", name: "Italiano" },
  pt: { flag: "🇵🇹", name: "Português" },
};
const langMeta = (code) => LEARNING_LANGS[code] || { flag: "🏳️", name: (code || "").toUpperCase() };

// Brand Logo
const LOGO_URL = "https://customer-assets.emergentagent.com/job_b31fc984-f212-4811-9faa-4930fd1aea96/artifacts/xtug1gme_image.png";

export default function Dashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, getAuthHeader, refreshUser } = useAuth();
  const { t } = useTranslation();
  const [progress, setProgress] = useState(null);
  const [stats, setStats] = useState(null);
  const [statsDialogLesson, setStatsDialogLesson] = useState(null);
  const [isOverallDialogOpen, setIsOverallDialogOpen] = useState(false);
  const [packages, setPackages] = useState([]);
  const [bundles, setBundles] = useState([]);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmItem, setConfirmItem] = useState(null);
  const [confirmType, setConfirmType] = useState("package");
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [startingSession, setStartingSession] = useState(false);
  const [listeningLessonId, setListeningLessonId] = useState(null);
  // Çoklu dil (Path A)
  const [switching, setSwitching] = useState(false);
  const [addLangOpen, setAddLangOpen] = useState(false);
  const [addLang, setAddLang] = useState(null);
  const [addPackages, setAddPackages] = useState([]);
  const [addBundles, setAddBundles] = useState([]);
  const [addLoading, setAddLoading] = useState(false);

  const enrolledLangs = (user?.enrollments && Object.keys(user.enrollments).length)
    ? Object.keys(user.enrollments)
    : (user?.learning_language ? [user.learning_language] : []);
  const activeLang = user?.learning_language || "en";

  // Bir dilin KULLANILABİLİR paketi var mı? (oturum hakkı var VE süresi dolmamış)
  // AKTİF dil için CANLI `progress` (my-progress) verisi kullanılır — çünkü AuthContext'teki
  // `user.package_dates` login anında yüklenir ve ders yapıldıkça güncellenmez (bayat kalır).
  // Aktif OLMAYAN diller için enrollment snapshot'ı (donuk ama doğru) kullanılır.
  const isLangUsable = (code) => {
    const now = new Date();
    if (code === activeLang) {
      if (!progress?.has_package) return false;
      const groups = progress.lessons_grouped || [];
      if (groups.length > 0) {
        return groups.some((g) => {
          const remaining = (g.total_sessions || 60) - (g.sessions_used || 0);
          const notExpired = g.end_date ? new Date(g.end_date) >= now : true;
          return remaining > 0 && notExpired;
        });
      }
      const total = progress.package?.total_sessions || 0;
      const used = progress.sessions_used || 0;
      const notExpired = progress.subscription_end ? new Date(progress.subscription_end) >= now : true;
      return total > 0 ? (used < total && notExpired) : true;
    }
    const pkgDates = user?.enrollments?.[code]?.package_dates;
    if (pkgDates && Object.keys(pkgDates).length > 0) {
      return Object.values(pkgDates).some((info) => {
        const remaining = (info.total_sessions || 60) - (info.sessions_used || 0);
        const notExpired = info.end_date ? new Date(info.end_date) >= now : true;
        return remaining > 0 && notExpired;
      });
    }
    return true; // veri yoksa varsayılan: kullanılabilir (yanlışlıkla "satın al"a düşürme)
  };

  // Çizgi ÜSTÜ (Dillerim / geçiş): SADECE hâlâ kullanılabilir (limiti VE süresi olan) kayıtlı diller.
  // Aktif/seçili dil olsa bile tükenmişse üstte tutulmaz; alta (satın al) düşer.
  const switchList = enrolledLangs.filter((c) => isLangUsable(c));
  // Çizgi ALTI (Satın al): hiç kayıtlı olmayan diller + kayıtlı ama tükenmiş/süresi dolmuş diller (aktif olsa bile)
  const availableToAdd = [
    ...Object.keys(LEARNING_LANGS).filter((c) => !enrolledLangs.includes(c)),
    ...enrolledLangs.filter((c) => !isLangUsable(c)),
  ];

  // Aktif dilin paketi(leri) SÜRESİ DOLMUŞ mu? (henüz satın almadan önce de "Paket Seçin"
  // ekranına yönlendirmek için). Süre dolduysa (kalan hak olsa bile) dashboard yerine satın
  // alma ekranı gösterilir; kullanılabilir (süresi dolmamış) en az bir paket varsa dashboard kalır.
  const activeLangExpired = (() => {
    if (!progress?.has_package) return false;
    const now = new Date();
    const groups = progress.lessons_grouped || [];
    if (groups.length > 0) {
      const anyValid = groups.some((g) => (g.end_date ? new Date(g.end_date) >= now : true));
      return !anyValid;
    }
    if (progress.subscription_end) return new Date(progress.subscription_end) < now;
    return false;
  })();

  const handleSwitchLanguage = async (lang) => {
    if (lang === activeLang || switching) return;
    setSwitching(true);
    try {
      await axios.post(`${API}/practice/switch-language`, { language: lang }, getAuthHeader());
      await refreshUser();
      setLoading(true);
      await fetchData();
      toast.success(`${langMeta(lang).flag} ${langMeta(lang).name} — ${t("language_switched")}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Switch failed");
    } finally {
      setSwitching(false);
    }
  };

  const openAddLanguage = async (lang) => {
    setAddLang(lang);
    setAddLangOpen(true);
    setAddLoading(true);
    try {
      const [pk, bd] = await Promise.all([
        axios.get(`${API}/practice/packages?language=${lang}`, getAuthHeader()),
        axios.get(`${API}/practice/bundles?language=${lang}`, getAuthHeader()).catch(() => ({ data: [] })),
      ]);
      setAddPackages(pk.data || []);
      setAddBundles(bd.data || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to load packages");
    } finally {
      setAddLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [location.key]);

  const fetchData = async () => {
    try {
      const [progressRes, packagesRes, bundlesRes, statsRes] = await Promise.all([
        axios.get(`${API}/practice/my-progress`, getAuthHeader()),
        axios.get(`${API}/practice/packages`, getAuthHeader()),
        axios.get(`${API}/practice/bundles`, getAuthHeader()).catch(() => ({ data: [] })),
        axios.get(`${API}/practice/my-stats`, getAuthHeader()).catch(() => ({ data: null }))
      ]);
      setProgress(progressRes.data);
      setPackages(packagesRes.data);
      setBundles(bundlesRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleListenLesson = async (lessonId) => {
    if (listeningLessonId) return;
    if (user && !user.profile_completed && user.role !== "admin") {
      navigate("/onboarding");
      return;
    }
    setListeningLessonId(lessonId);
    try {
      const response = await axios.post(
        `${API}/practice/lesson/teach`,
        { lesson_id: lessonId },
        getAuthHeader()
      );
      navigate(`/lesson/${lessonId}`, {
        state: {
          steps: response.data.steps,
          grammarTopic: response.data.grammar_topic,
        },
      });
    } catch (error) {
      if (error.response?.status === 403 && error.response?.data?.detail === "PROFILE_INCOMPLETE") {
        setListeningLessonId(null);
        navigate("/onboarding");
        return;
      }
      if (error.response?.data?.detail === "INSUFFICIENT_CREDITS") {
        toast.error(t("insufficient_credits"));
      } else {
        toast.error(error.response?.data?.detail || t("session_start_failed"));
      }
      setListeningLessonId(null);
    }
  };

  const handleStartSession = async (lessonId) => {
    if (startingSession) return;
    // Mandatory onboarding gate: profile must be completed before the first session.
    if (user && !user.profile_completed && user.role !== "admin") {
      navigate("/onboarding");
      return;
    }
    setStartingSession(true);
    try {
      const response = await axios.post(
        `${API}/practice/session/start`,
        { lesson_id: lessonId },
        getAuthHeader()
      );
      navigate(`/practice/${response.data.session_id}`, { 
        state: { 
          lesson: response.data.lesson,
          initialMessage: response.data.initial_message,
          preloadedAudio: response.data.audio  // Pre-generated audio from streaming
        }
      });
    } catch (error) {
      if (error.response?.status === 403 && error.response?.data?.detail === "PROFILE_INCOMPLETE") {
        setStartingSession(false);
        navigate("/onboarding");
        return;
      }
      toast.error(error.response?.data?.detail || t("session_start_failed"));
      setStartingSession(false);
    }
  };

  const openConfirm = (item, type, closeAddLang = false) => {
    if (closeAddLang) setAddLangOpen(false);
    setConfirmItem(item);
    setConfirmType(type);
    setConfirmOpen(true);
  };

  const handleConfirmProceed = () => {
    if (!confirmItem) return;
    if (confirmType === "bundle") handleBundlePurchase(confirmItem.id);
    else handlePurchase(confirmItem.id);
  };

  const handlePurchase = async (packageId) => {
    setCheckoutLoading(true);
    try {
      if (PAYMENT_PROVIDER === "monri") {
        const response = await axios.post(
          `${API}/payments/monri/checkout`,
          { package_id: packageId, origin_url: window.location.origin },
          getAuthHeader()
        );
        submitMonriForm(response.data.form_url, response.data.fields);
        return; // sayfa Monri'ye yönlenecek
      }
      const response = await axios.post(
        `${API}/payments/checkout`,
        { package_id: packageId, origin_url: window.location.origin },
        getAuthHeader()
      );
      window.location.href = response.data.checkout_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || t("payment_start_failed"));
    } finally {
      setCheckoutLoading(false);
    }
  };

  const handleBundlePurchase = async (bundleId) => {
    setCheckoutLoading(true);
    try {
      if (PAYMENT_PROVIDER === "monri") {
        const response = await axios.post(
          `${API}/payments/monri/bundle-checkout`,
          { bundle_id: bundleId, origin_url: window.location.origin },
          getAuthHeader()
        );
        submitMonriForm(response.data.form_url, response.data.fields);
        return;
      }
      const response = await axios.post(
        `${API}/payments/bundle-checkout`,
        { bundle_id: bundleId, origin_url: window.location.origin },
        getAuthHeader()
      );
      window.location.href = response.data.checkout_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || t("payment_start_failed"));
    } finally {
      setCheckoutLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("tr-TR");
  };

  const getProgressPercentage = () => {
    if (!progress?.has_package) return 0;
    const total = progress.package?.total_sessions || 60;
    const used = progress.sessions_used || 0;
    return Math.round((used / total) * 100);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black">
      {/* Ders oluşturuluyor overlay'i */}
      {listeningLessonId && (
        <div
          className="fixed inset-0 z-[100] flex flex-col items-center justify-center bg-black/80 backdrop-blur-sm"
          data-testid="lesson-loading-overlay"
        >
          <Loader2 className="w-12 h-12 text-red-500 animate-spin mb-5" />
          <p className="text-white text-lg font-semibold px-6 text-center">{t("lesson_preparing")}</p>
          <p className="text-neutral-400 text-sm mt-2 px-6 text-center">{t("lesson_please_wait")}</p>
        </div>
      )}
      {/* Header */}
      <header className="bg-neutral-900 border-b border-neutral-800 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Konuşuyoruz Biz" className="h-8 shrink-0 -ml-0 sm:-ml-4" />
          </div>
          <div className="flex items-center gap-2 sm:gap-4">
            <span className="hidden sm:inline text-neutral-400">{t("hello")} <strong className="text-white">{user?.full_name}</strong></span>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => navigate("/account")}
              className="text-neutral-400 hover:text-white hover:bg-white/10"
              data-testid="my-account-btn"
            >
              <UserCircle className="w-4 h-4 mr-0 sm:mr-2" />
              <span className="hidden sm:inline">{t("my_account")}</span>
            </Button>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={logout}
              className="text-neutral-400 hover:text-white hover:bg-white/10"
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4 mr-0 sm:mr-2" />
              <span className="hidden sm:inline">{t("logout")}</span>
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Welcome Section + Language Switcher */}
        <div className="mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-white font-['Nunito']">
              {t("welcome")} 
            </h1>
            <p className="text-neutral-400 mt-1">{t("continue_practice")}</p>
          </div>
          {/* Dil Switcher (mobil uyumlu dropdown) */}
          {enrolledLangs.length > 0 && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  className="h-11 px-4 rounded-full border-neutral-700 bg-neutral-900 text-white hover:bg-neutral-800 hover:text-white flex items-center gap-2 self-start sm:self-auto"
                  data-testid="language-switcher-trigger"
                  disabled={switching}
                >
                  {switching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Globe className="w-4 h-4 text-red-500" />}
                  <span className="text-lg leading-none">{langMeta(activeLang).flag}</span>
                  <span className="font-semibold">{langMeta(activeLang).name}</span>
                  <ChevronDown className="w-4 h-4 text-neutral-400" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-60 bg-neutral-900 border-neutral-700 text-white">
                {switchList.length > 0 && (
                  <DropdownMenuLabel className="text-neutral-400">{t("my_languages")}</DropdownMenuLabel>
                )}
                {switchList.map((code) => (
                  <DropdownMenuItem
                    key={code}
                    onClick={() => handleSwitchLanguage(code)}
                    className="cursor-pointer focus:bg-neutral-800 focus:text-white flex items-center gap-2"
                    data-testid={`switch-language-${code}`}
                  >
                    <span className="text-lg leading-none">{langMeta(code).flag}</span>
                    <span className="flex-1">{langMeta(code).name}</span>
                    {code === activeLang && <Check className="w-4 h-4 text-red-500" />}
                  </DropdownMenuItem>
                ))}
                {availableToAdd.length > 0 && (
                  <>
                    {switchList.length > 0 && <DropdownMenuSeparator className="bg-neutral-700" />}
                    <DropdownMenuLabel className="text-red-500" data-testid="buy-language-label">{t("buy")}</DropdownMenuLabel>
                    {availableToAdd.map((code) => (
                      <DropdownMenuItem
                        key={code}
                        onClick={() => openAddLanguage(code)}
                        className="cursor-pointer focus:bg-neutral-800 text-red-400 focus:text-red-300 flex items-center gap-2"
                        data-testid={`add-language-${code}`}
                      >
                        <Plus className="w-4 h-4" />
                        <span className="text-lg leading-none">{langMeta(code).flag}</span>
                        <span className="flex-1">{langMeta(code).name}</span>
                      </DropdownMenuItem>
                    ))}
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>

        {progress?.has_package && user?.is_active !== false && !activeLangExpired ? (
          /* Active (non-passive) user with a USABLE package. NOTE: a user who is passive because
             ALL packages are depleted (is_active === false) OR whose active language package is
             DATE-EXPIRED skips the dashboard and sees the package-selection screen directly,
             ignoring any remaining package days/sessions. */
          <div className="space-y-6">
            {/* Gelişim Raporu Şeridi */}
            {stats && stats.overall && stats.overall.total_sessions > 0 && (
              <button
                type="button"
                onClick={() => setIsOverallDialogOpen(true)}
                className="group w-full flex items-center justify-between gap-4 p-4 rounded-xl bg-neutral-900 border border-neutral-800 transition-all duration-200 hover:scale-[1.01] hover:border-red-500/60 hover:shadow-lg hover:shadow-red-500/10 cursor-pointer text-left"
                data-testid="overall-performance-strip"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-red-600/20 text-red-500 flex items-center justify-center">
                    <BarChart3 className="w-5 h-5" />
                  </div>
                  <p className="text-base text-white font-semibold transition-colors duration-200 group-hover:text-red-500">{t("progress_report")}</p>
                </div>
                <div className="text-right">
                  {(() => {
                    const acc = stats.overall.average_accuracy;
                    const colorCls = acc >= 80
                      ? "text-emerald-400"
                      : acc >= 50
                      ? "text-amber-400"
                      : "text-red-400";
                    return (
                      <span className={`text-2xl font-bold ${colorCls}`} data-testid="overall-accuracy">
                        {acc}%
                      </span>
                    );
                  })()}
                </div>
              </button>
            )}
            {progress.lessons_grouped?.length > 0 ? (
              /* Bundle/Combo user OR single package - show each package with its own banner */
              progress.lessons_grouped.map((group) => {
                const remainingSessions = Math.max(0, (group.total_sessions || 60) - (group.sessions_used || 0));
                const remainingDays = group.end_date 
                  ? Math.max(0, Math.ceil((new Date(group.end_date) - new Date()) / (1000 * 60 * 60 * 24)))
                  : (group.duration_days || 60);
                const usedPercent = Math.round(((group.sessions_used || 0) / (group.total_sessions || 60)) * 100);
                
                return (
                <div key={group.package_id} className="space-y-6" data-testid={`package-section-${group.level}`}>
                  {/* Progress Card */}
                  <Card className="w-full border-0 shadow-lg bg-gradient-to-br from-red-600 to-red-800 text-white">
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between flex-wrap gap-4">
                        <div>
                          <p className="text-red-200 text-sm font-medium">{t("active_package")}</p>
                          <h2 className="text-2xl font-bold mt-1 font-['Nunito']">{group.package_name}</h2>
                          <div className="flex flex-col gap-1 mt-3">
                            <div className="flex items-center gap-2">
                              <Calendar className="w-4 h-4 text-red-200" />
                              <span className="text-sm text-red-100">{t("purchase_date")}: {formatDate(group.purchase_date)}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Calendar className="w-4 h-4 text-red-200" />
                              <span className="text-sm text-red-100">{t("start_date")}: {group.start_date ? formatDate(group.start_date) : '—'}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Calendar className="w-4 h-4 text-red-200" />
                              <span className="text-sm text-red-100">{t("end_date")}: {group.end_date ? formatDate(group.end_date) : '—'}</span>
                            </div>
                            <div className="flex items-center gap-2 mt-1">
                              <Clock className="w-4 h-4 text-red-200" />
                              <span className="text-sm text-red-100">{t("remaining_days")}: {remainingDays} {t("days")}</span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-6">
                          <div className="text-center">
                            <div className="text-[11px] leading-tight text-red-100/90 mb-2 space-y-0.5" data-testid="credit-rules-info">
                              <p>{t("credit_rule_lesson")}</p>
                              <p>{t("credit_rule_practice")}</p>
                            </div>
                            <p className="text-red-200 text-sm">{t("remaining_credits")}</p>
                            <p className="text-3xl font-bold text-white">
                              {remainingSessions} / {group.total_sessions || 60}
                            </p>
                          </div>
                          <div className="w-20 h-20 relative">
                            <svg className="progress-ring w-20 h-20">
                              <circle className="text-red-900" strokeWidth="6" stroke="currentColor" fill="transparent" r="32" cx="40" cy="40" />
                              <circle className="text-white progress-ring-circle" strokeWidth="6" strokeDasharray={`${2 * Math.PI * 32}`} strokeDashoffset={`${2 * Math.PI * 32 * (1 - usedPercent / 100)}`} strokeLinecap="round" stroke="currentColor" fill="transparent" r="32" cx="40" cy="40" />
                            </svg>
                            <div className="absolute inset-0 flex items-center justify-center">
                              <span className="text-lg font-bold">{usedPercent}%</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Lessons List */}
                  <Card className="w-full border-neutral-800 bg-neutral-900 shadow-lg" data-testid={`lessons-card-${group.level}`}>
                    <CardHeader>
                      <CardTitle className="text-xl font-bold text-white font-['Nunito'] text-center">
                        {t("select_lesson")}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {group.lessons.map((lesson) => {
                          const lessonStat = stats?.lessons?.find(l => l.lesson_id === lesson.id);
                          const acc = lessonStat?.average_accuracy;
                          const colorCls = acc >= 80
                            ? "bg-emerald-500/20 text-emerald-400 border-emerald-500/30"
                            : acc >= 50
                            ? "bg-amber-500/20 text-amber-400 border-amber-500/30"
                            : "bg-red-500/20 text-red-400 border-red-500/30";
                          const accuracyBadge = lessonStat ? (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                setStatsDialogLesson({ ...lessonStat, lesson_title: lessonStat.lesson_title || lesson.grammar_topic });
                              }}
                              className={`px-2 py-0.5 rounded-md text-xs font-semibold border cursor-pointer transition-all duration-200 hover:scale-125 hover:font-bold hover:ring-2 hover:ring-red-500 hover:ring-offset-2 hover:ring-offset-neutral-900 ${colorCls}`}
                              title="Detayları gör"
                              data-testid={`lesson-accuracy-${lesson.id}`}
                            >
                              {acc}%
                            </button>
                          ) : null;
                          const attendedIcon = progress.attended_lesson_ids?.includes(lesson.id) ? (
                            <CheckCircle className="w-5 h-5 text-green-500" data-testid={`lesson-attended-${lesson.id}`} />
                          ) : null;

                          return (
                              <div
                                key={lesson.id}
                                className="p-4 rounded-xl bg-neutral-800 transition-colors"
                                data-testid={`lesson-item-${lesson.id}`}
                              >
                                <div className="flex items-start justify-between gap-3">
                                  <div className="flex items-start gap-3 flex-1 min-w-0">
                                    <div className="w-8 h-8 rounded-lg bg-red-600/20 text-red-500 flex items-center justify-center font-semibold text-sm shrink-0">
                                      {lesson.order_number}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <p className="font-medium text-white break-words">{lesson.grammar_topic}</p>
                                      <p className="text-sm text-neutral-400 break-words">{lesson.description}</p>
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-2 shrink-0 mt-1">
                                    {accuracyBadge}
                                    {attendedIcon}
                                  </div>
                                </div>
                                <div className="flex flex-wrap gap-2 mt-3 sm:pl-11">
                                  <Button
                                    type="button"
                                    onClick={() => handleListenLesson(lesson.id)}
                                    disabled={listeningLessonId === lesson.id}
                                    className="bg-neutral-700 hover:bg-neutral-600 text-white h-9 px-4 rounded-lg text-sm font-semibold"
                                    data-testid={`listen-lesson-${lesson.id}`}
                                  >
                                    {listeningLessonId === lesson.id
                                      ? <Loader2 className="w-4 h-4 animate-spin mr-2" />
                                      : <BookOpen className="w-4 h-4 mr-2" />}
                                    {t("listen_lesson")}
                                  </Button>
                                  <Button
                                    type="button"
                                    onClick={() => handleStartSession(lesson.id)}
                                    disabled={startingSession}
                                    className="bg-red-600 hover:bg-red-700 text-white h-9 px-4 rounded-lg text-sm font-semibold"
                                    data-testid={`start-practice-${lesson.id}`}
                                  >
                                    {t("start_practice")}
                                    <ChevronRight className="w-4 h-4 ml-1" />
                                  </Button>
                                </div>
                              </div>
                            );
                        })}
                      </div>
                    </CardContent>
                  </Card>
                </div>
              );
              })
            ) : (
              /* Fallback: single package without grouped data */
              <>
                <Card className="w-full border-0 shadow-lg bg-gradient-to-br from-red-600 to-red-800 text-white">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between flex-wrap gap-4">
                      <div>
                        <p className="text-red-200 text-sm font-medium">{t("active_package")}</p>
                        <h2 className="text-2xl font-bold mt-1 font-['Nunito']">{progress.package?.name}</h2>
                        <div className="flex items-center gap-4 mt-2">
                          <div className="flex items-center gap-2">
                            <Calendar className="w-4 h-4 text-red-200" />
                            <span className="text-sm text-red-100">{t("end_date")}: {formatDate(progress.subscription_end)}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Clock className="w-4 h-4 text-red-200" />
                            <span className="text-sm text-red-100">
                              {t("remaining_days")}: {Math.max(0, Math.ceil((new Date(progress.subscription_end) - new Date()) / (1000 * 60 * 60 * 24)))} {t("days")}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-6">
                        <div className="text-center">
                          <div className="text-[11px] leading-tight text-red-100/90 mb-2 space-y-0.5" data-testid="credit-rules-info-legacy">
                            <p>{t("credit_rule_lesson")}</p>
                            <p>{t("credit_rule_practice")}</p>
                          </div>
                          <p className="text-red-200 text-sm">{t("remaining_credits")}</p>
                          <p className="text-3xl font-bold text-white">
                            {Math.max(0, (progress.package?.total_sessions || 60) - (progress.sessions_used || 0))} / {progress.package?.total_sessions || 60}
                          </p>
                        </div>
                        <div className="w-20 h-20 relative">
                          <svg className="progress-ring w-20 h-20">
                            <circle className="text-red-900" strokeWidth="6" stroke="currentColor" fill="transparent" r="32" cx="40" cy="40" />
                            <circle className="text-white progress-ring-circle" strokeWidth="6" strokeDasharray={`${2 * Math.PI * 32}`} strokeDashoffset={`${2 * Math.PI * 32 * (1 - getProgressPercentage() / 100)}`} strokeLinecap="round" stroke="currentColor" fill="transparent" r="32" cx="40" cy="40" />
                          </svg>
                          <div className="absolute inset-0 flex items-center justify-center">
                            <span className="text-lg font-bold">{getProgressPercentage()}%</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="w-full border-neutral-800 bg-neutral-900 shadow-lg" data-testid="lessons-card">
                  <CardHeader>
                    <CardTitle className="text-xl font-bold text-white font-['Nunito'] text-center">
                      {t("select_lesson")}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {progress.lessons?.map((lesson) => (
                        <div 
                          key={lesson.id}
                          className="flex items-start justify-between gap-3 p-4 rounded-xl bg-neutral-800 hover:bg-neutral-700 cursor-pointer transition-colors group"
                          onClick={() => handleStartSession(lesson.id)}
                          data-testid={`lesson-item-${lesson.id}`}
                        >
                          <div className="flex items-start gap-3 flex-1 min-w-0">
                            <div className="w-8 h-8 rounded-lg bg-red-600/20 text-red-500 flex items-center justify-center font-semibold text-sm shrink-0">
                              {lesson.order_number}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-white break-words">{lesson.grammar_topic}</p>
                              <p className="text-sm text-neutral-400 break-words">{lesson.description}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 shrink-0 mt-1">
                            {(() => {
                              const lessonStat = stats?.lessons?.find(l => l.lesson_id === lesson.id);
                              if (!lessonStat) return null;
                              const acc = lessonStat.average_accuracy;
                              const colorCls = acc >= 80
                                ? "bg-emerald-500/20 text-emerald-400 border-emerald-500/30"
                                : acc >= 50
                                ? "bg-amber-500/20 text-amber-400 border-amber-500/30"
                                : "bg-red-500/20 text-red-400 border-red-500/30";
                              return (
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setStatsDialogLesson({ ...lessonStat, lesson_title: lessonStat.lesson_title || lesson.grammar_topic });
                                  }}
                                  className={`px-2 py-0.5 rounded-md text-xs font-semibold border cursor-pointer transition-all duration-200 hover:scale-125 hover:font-bold hover:ring-2 hover:ring-red-500 hover:ring-offset-2 hover:ring-offset-neutral-900 ${colorCls}`}
                                  title="Detayları gör"
                                  data-testid={`lesson-accuracy-${lesson.id}`}
                                >
                                  {acc}%
                                </button>
                              );
                            })()}
                            <ChevronRight className="w-5 h-5 text-neutral-500 group-hover:text-red-500 transition-colors" />
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </>
            )}
          </div>
        ) : (
          /* No active package - Show packages */
          <div>
            {/* Combo/Bundle Section */}
            {bundles.length > 0 && (
              <div className="mb-12">
                <div className="text-center mb-8">
                  <h2 className="text-2xl font-bold text-white font-['Nunito']">{t("combo_packages")}</h2>
                  <p className="text-neutral-400 mt-2">{t("combo_subtitle")}</p>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {bundles.map((bundle) => (
                    <Card 
                      key={bundle.id} 
                      className="border-amber-600/30 bg-neutral-900 shadow-lg hover:border-amber-500/60 transition-all hover:-translate-y-1 relative overflow-hidden"
                      data-testid={`bundle-card-${bundle.id}`}
                    >
                      <div className="absolute top-0 right-0 bg-amber-600 text-white text-xs font-bold px-3 py-1 rounded-bl-lg">
                        %{bundle.discount_percentage} {t("discount")}
                      </div>
                      <CardHeader>
                        <div className="flex items-center gap-2 mb-2">
                          <Gift className="w-5 h-5 text-amber-500" />
                          <Badge className="bg-amber-600/20 text-amber-500 border-amber-600/30">{t("combo")}</Badge>
                        </div>
                        <CardTitle className="text-xl font-bold text-white font-['Nunito']">{bundle.name}</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-3 mb-4">
                          <p className="text-sm text-neutral-400 font-medium">{t("included_packages")}:</p>
                          <BundlePackageDetails packages={bundle.packages} />
                        </div>
                        <div className="flex items-center gap-3 mb-6">
                          <span className="text-neutral-500 line-through text-lg">€{bundle.original_price}</span>
                          <span className="text-2xl font-bold text-amber-400">€{bundle.price}</span>
                        </div>
                        <Button 
                          className="w-full bg-amber-600 hover:bg-amber-700 text-white rounded-lg h-12"
                          onClick={() => openConfirm(bundle, "bundle")}
                          disabled={checkoutLoading}
                          data-testid={`purchase-bundle-btn-${bundle.id}`}
                        >
                          <CreditCard className="w-4 h-4 mr-2" />
                          {t("buy_combo")}
                        </Button>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {/* Single Packages Section */}
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-white font-['Nunito']">{t("select_package")}</h2>
              <p className="text-neutral-400 mt-2">{t("select_package_subtitle")}</p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {packages.map((pkg) => (
                <Card 
                  key={pkg.id} 
                  className="border-neutral-800 bg-neutral-900 shadow-lg hover:border-red-600/50 transition-all hover:-translate-y-1"
                  data-testid={`package-card-${pkg.id}`}
                >
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <Badge className="bg-red-600/20 text-red-500 border-red-600/30">{pkg.level}</Badge>
                      <span className="text-2xl font-bold text-white">€{pkg.price}</span>
                    </div>
                    <CardTitle className="text-xl font-bold text-white font-['Nunito'] mt-3">{pkg.name}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-3 mb-6">
                      <li className="flex items-center gap-2 text-neutral-300">
                        <Clock className="w-4 h-4 text-red-500" />
                        <span>{pkg.total_sessions} {t("total_credits_label")} • {pkg.session_duration_minutes} {t("minutes_per_session")}</span>
                      </li>
                      <li className="flex items-center gap-2 text-neutral-300">
                        <Calendar className="w-4 h-4 text-red-500" />
                        <span>{pkg.duration_days} {t("days_access")}</span>
                      </li>
                      <li className="flex items-center gap-2 text-neutral-300">
                        <BookOpen className="w-4 h-4 text-red-500" />
                        <span>{t("ai_practice")}</span>
                      </li>
                    </ul>
                    <Button 
                      className="w-full bg-red-600 hover:bg-red-700 text-white rounded-lg h-12"
                      onClick={() => openConfirm(pkg, "package")}
                      disabled={checkoutLoading}
                      data-testid={`purchase-btn-${pkg.id}`}
                    >
                      <CreditCard className="w-4 h-4 mr-2" />
                      {t("buy")}
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}
      </main>

      {/* Lesson Stats Detail Dialog */}
      <Dialog open={!!statsDialogLesson} onOpenChange={(open) => { if (!open) setStatsDialogLesson(null); }}>
        <DialogContent className="max-w-lg bg-neutral-900 border-neutral-800 max-h-[85vh] overflow-y-auto" data-testid="lesson-stats-dialog">
          <DialogHeader>
            <DialogTitle className="text-white font-['Nunito']">
              {statsDialogLesson?.lesson_title || "—"}
            </DialogTitle>
          </DialogHeader>
          {statsDialogLesson && (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-3">
                <div className="p-3 rounded-lg bg-neutral-800 border border-neutral-700">
                  <p className="text-[11px] text-neutral-400">{t("sessions_count")}</p>
                  <p className="text-xl font-bold text-white">{statsDialogLesson.session_count}</p>
                </div>
                <div className="p-3 rounded-lg bg-neutral-800 border border-neutral-700">
                  <p className="text-[11px] text-neutral-400">{t("average_score")}</p>
                  {(() => {
                    const acc = statsDialogLesson.average_accuracy;
                    const colorCls = acc >= 80 ? "text-emerald-400" : acc >= 50 ? "text-amber-400" : "text-red-400";
                    return <p className={`text-xl font-bold ${colorCls}`}>{acc}%</p>;
                  })()}
                </div>
                <div className="p-3 rounded-lg bg-neutral-800 border border-neutral-700">
                  <p className="text-[11px] text-neutral-400">{t("best_score")}</p>
                  <p className="text-xl font-bold text-emerald-400">{statsDialogLesson.best_accuracy}%</p>
                </div>
              </div>

              <div className="space-y-2">
                <p className="text-sm text-neutral-300 font-semibold">{t("session_history")}</p>
                {statsDialogLesson.sessions.map((s, idx) => {
                  const sAcc = s.accuracy;
                  const colorCls = sAcc >= 80
                    ? "bg-emerald-500/20 text-emerald-400 border-emerald-500/30"
                    : sAcc >= 50
                    ? "bg-amber-500/20 text-amber-400 border-amber-500/30"
                    : "bg-red-500/20 text-red-400 border-red-500/30";
                  const dateStr = s.started_at ? new Date(s.started_at).toLocaleDateString() : "—";
                  return (
                    <div key={idx} className="flex items-center justify-between gap-3 p-3 rounded-lg bg-neutral-800 border border-neutral-700">
                      <div>
                        <p className="text-sm text-white font-medium">{idx + 1}. {t("sessions_count")}</p>
                        <p className="text-xs text-neutral-500">{dateStr}</p>
                      </div>
                      <span className={`px-2 py-1 rounded-md text-sm font-bold border ${colorCls}`}>
                        {sAcc}%
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Overall (full) Stats Dialog */}
      <Dialog open={isOverallDialogOpen} onOpenChange={setIsOverallDialogOpen}>
        <DialogContent className="max-w-3xl bg-neutral-900 border-neutral-800 max-h-[85vh] overflow-y-auto" data-testid="overall-stats-dialog">
          <DialogHeader>
            <DialogTitle className="text-white font-['Nunito']">
              {t("progress_report")}
            </DialogTitle>
          </DialogHeader>
          {stats && stats.overall && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="p-4 rounded-lg bg-neutral-800 border border-neutral-700">
                  <p className="text-xs text-neutral-400">{t("total_sessions")}</p>
                  <p className="text-2xl font-bold text-white">{stats.overall.total_sessions}</p>
                </div>
                <div className="p-4 rounded-lg bg-neutral-800 border border-neutral-700">
                  <p className="text-xs text-neutral-400">{t("average_score")}</p>
                  {(() => {
                    const acc = stats.overall.average_accuracy;
                    const colorCls = acc >= 80 ? "text-emerald-400" : acc >= 50 ? "text-amber-400" : "text-red-400";
                    return <p className={`text-2xl font-bold ${colorCls}`}>{acc}%</p>;
                  })()}
                </div>
              </div>

              {stats.lessons.length > 0 && (
                <div className="space-y-2">
                  <p className="text-sm text-neutral-300 font-semibold mt-2">{t("lesson_breakdown")}</p>
                  {stats.lessons.map((l) => {
                    const acc = l.average_accuracy;
                    const colorBg = acc >= 80
                      ? "bg-emerald-500/20 text-emerald-400 border-emerald-500/30"
                      : acc >= 50
                      ? "bg-amber-500/20 text-amber-400 border-amber-500/30"
                      : "bg-red-500/20 text-red-400 border-red-500/30";
                    return (
                      <div key={l.lesson_id} className="p-3 rounded-lg bg-neutral-800 border border-neutral-700">
                        <div className="flex items-center justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-white truncate">{l.lesson_title}</p>
                            <p className="text-xs text-neutral-500 mt-0.5">
                              {l.session_count} {t("sessions_count")} • {t("best_score")} {l.best_accuracy}%
                            </p>
                          </div>
                          <span className={`px-2 py-1 rounded-md text-sm font-bold border ${colorBg}`}>
                            {acc}%
                          </span>
                        </div>
                        {l.sessions.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-3">
                            {l.sessions.map((s, idx) => {
                              const sAcc = s.accuracy;
                              const sColor = sAcc >= 80
                                ? "bg-emerald-600/30 text-emerald-300"
                                : sAcc >= 50
                                ? "bg-amber-600/30 text-amber-300"
                                : "bg-red-600/30 text-red-300";
                              const dateStr = s.started_at ? new Date(s.started_at).toLocaleDateString() : "—";
                              return (
                                <span
                                  key={idx}
                                  className={`text-[11px] px-2 py-0.5 rounded ${sColor}`}
                                  title={`${dateStr} • ${s.correct}/${s.total}`}
                                >
                                  {idx + 1}. {sAcc}%
                                </span>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Başka dil ekle: satın alma modalı */}
      <Dialog open={addLangOpen} onOpenChange={setAddLangOpen}>
        <DialogContent className="bg-neutral-900 border-neutral-800 text-white max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="add-language-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-white">
              <span className="text-2xl leading-none">{langMeta(addLang).flag}</span>
              {t("add_language_title")} — {langMeta(addLang).name}
            </DialogTitle>
          </DialogHeader>
          {addLoading ? (
            <div className="py-12 flex justify-center">
              <Loader2 className="w-8 h-8 animate-spin text-red-600" />
            </div>
          ) : (
            <div className="space-y-6">
              {addBundles.length === 0 && addPackages.length === 0 && (
                <p className="text-neutral-400 text-center py-8">—</p>
              )}
              {addBundles.length > 0 && (
                <div className="space-y-3">
                  <h3 className="text-sm font-semibold text-amber-400">{t("combo_packages")}</h3>
                  {addBundles.map((bundle) => (
                    <div key={bundle.id} className="p-3 rounded-lg border border-amber-600/30 bg-neutral-800/50" data-testid={`add-bundle-${bundle.id}`}>
                      <div className="flex items-center justify-between gap-3">
                        <div className="min-w-0">
                          <p className="font-semibold text-white truncate">{bundle.name}</p>
                          <p className="text-xs text-neutral-400">
                            <span className="line-through mr-1 text-neutral-500">€{bundle.original_price}</span>
                            <span className="text-amber-400 font-semibold">€{bundle.price}</span>
                            <span className="ml-2 text-green-500 font-semibold">%{bundle.discount_percentage} {t("discount")}</span>
                          </p>
                        </div>
                        <Button
                          size="sm"
                          onClick={() => openConfirm(bundle, "bundle", true)}
                          disabled={checkoutLoading}
                          className="bg-amber-600 hover:bg-amber-700 text-white shrink-0"
                          data-testid={`add-bundle-buy-${bundle.id}`}
                        >
                          {t("buy_combo")}
                        </Button>
                      </div>
                      <div className="mt-3">
                        <BundlePackageDetails packages={bundle.packages} />
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {addPackages.length > 0 && (
                <div className="space-y-3">
                  <h3 className="text-sm font-semibold text-red-400">{t("select_package")}</h3>
                  {addPackages.map((pkg) => (
                    <div key={pkg.id} className="p-3 rounded-lg border border-neutral-700 bg-neutral-800/50" data-testid={`add-package-${pkg.id}`}>
                      <div className="flex items-center justify-between gap-3">
                        <div className="min-w-0">
                          <p className="font-semibold text-white truncate">{pkg.level} — {pkg.name}</p>
                          <p className="text-xs text-neutral-400">€{pkg.price}</p>
                        </div>
                        <Button
                          size="sm"
                          onClick={() => openConfirm(pkg, "package", true)}
                          disabled={checkoutLoading}
                          className="bg-red-600 hover:bg-red-700 text-white shrink-0"
                          data-testid={`add-package-buy-${pkg.id}`}
                        >
                          {t("buy")}
                        </Button>
                      </div>
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-2 text-xs text-neutral-400" data-testid={`add-package-details-${pkg.id}`}>
                        <span className="flex items-center gap-1">
                          <Clock className="w-3.5 h-3.5 text-red-500 shrink-0" />
                          {pkg.total_sessions} {t("total_credits_label")} • {pkg.session_duration_minutes} {t("minutes_per_session")}
                        </span>
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3.5 h-3.5 text-red-500 shrink-0" />
                          {pkg.duration_days} {t("days_access")}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <PurchaseConfirmModal
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        item={confirmItem}
        itemType={confirmType}
        onConfirm={handleConfirmProceed}
        loading={checkoutLoading}
      />
    </div>
  );
}
